import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogPortal, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  submitFeedback,
  submitReport,
  REPORT_REASONS,
  type ReportTargetType,
  type ReportReason,
} from "@/lib/services/community";

// ─── Step types ───────────────────────────────────────────────────────────────

type ModalStep = "pick" | "form" | "success";
type OptionKey = "feature" | "problem" | "document";
type ProblemKind = "bug" | "member";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

// ─── Step 1: Option picker ────────────────────────────────────────────────────

function OptionCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-4 p-4 rounded-2xl border border-gray-200 hover:border-[#CA8A04] hover:bg-[#CA8A04]/5 transition-[transform,border-color,background-color,color] duration-150 text-left group active:scale-[0.97]"
    >
      <span className="text-2xl mt-0.5">{icon}</span>
      <div>
        <p className="font-semibold text-gray-900 group-hover:text-[#CA8A04] transition-colors">
          {title}
        </p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </button>
  );
}

// ─── Feature request form ─────────────────────────────────────────────────────

function FeatureRequestForm({ onSuccess }: { onSuccess: () => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: () => submitFeedback({ type: "feature_request", title, body }),
    onSuccess,
    onError: () => toast.error("Something went wrong. Please try again."),
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="feat-title">Title</Label>
        <Input
          id="feat-title"
          placeholder="Short summary of your idea"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="feat-body">Description</Label>
        <Textarea
          id="feat-body"
          placeholder="What problem does this solve? How would it work?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
        />
      </div>
      <Button
        onClick={() => mutate()}
        disabled={!title.trim() || !body.trim() || isPending}
        className="w-full bg-[#CA8A04] hover:bg-[#B8860B] text-white rounded-xl"
      >
        {isPending ? "Submitting…" : "Submit Request"}
      </Button>
    </div>
  );
}

// ─── Bug report form ──────────────────────────────────────────────────────────

function BugReportForm({ onSuccess }: { onSuccess: () => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: () => submitFeedback({ type: "bug_report", title, body }),
    onSuccess,
    onError: () => toast.error("Something went wrong. Please try again."),
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="bug-title">What went wrong?</Label>
        <Input
          id="bug-title"
          placeholder="Brief description of the bug"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="bug-body">Steps to reproduce</Label>
        <Textarea
          id="bug-body"
          placeholder="What were you doing when this happened? What did you expect to happen?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
        />
      </div>
      <Button
        onClick={() => mutate()}
        disabled={!title.trim() || !body.trim() || isPending}
        className="w-full bg-[#CA8A04] hover:bg-[#B8860B] text-white rounded-xl"
      >
        {isPending ? "Submitting…" : "Report Bug"}
      </Button>
    </div>
  );
}

// ─── Member report form ───────────────────────────────────────────────────────

function MemberReportForm({ onSuccess }: { onSuccess: () => void }) {
  const [targetType, setTargetType] = useState<ReportTargetType>("anonhandle");
  const [reason, setReason] = useState<ReportReason | "">("");
  const [details, setDetails] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      submitReport({
        target_type: targetType,
        reason: reason as ReportReason,
        details,
      }),
    onSuccess,
    onError: () => toast.error("Something went wrong. Please try again."),
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>What are you reporting?</Label>
        <Select
          value={targetType}
          onValueChange={(v) => setTargetType(v as ReportTargetType)}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="anonhandle">A member</SelectItem>
            <SelectItem value="comment">A comment</SelectItem>
            <SelectItem value="document">A document</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Reason</Label>
        <Select
          value={reason}
          onValueChange={(v) => setReason(v as ReportReason)}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue placeholder="Select a reason" />
          </SelectTrigger>
          <SelectContent>
            {REPORT_REASONS.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="report-details">
          Details{" "}
          <span className="text-gray-400 font-normal">(recommended)</span>
        </Label>
        <Textarea
          id="report-details"
          placeholder="Describe what happened. Include any relevant context (e.g. handle name, document title, quote from comment)."
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={4}
        />
      </div>
      <Button
        onClick={() => mutate()}
        disabled={!reason || isPending}
        className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl"
      >
        {isPending ? "Submitting…" : "Submit Report"}
      </Button>
    </div>
  );
}

// ─── Problem picker ───────────────────────────────────────────────────────────

function ProblemPicker({
  onSelect,
}: {
  onSelect: (kind: ProblemKind) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">What kind of problem?</p>
      <OptionCard
        icon="🐛"
        title="Report a Bug"
        description="Something on the platform isn't working correctly."
        onClick={() => onSelect("bug")}
      />
      <OptionCard
        icon="🚩"
        title="Report a Member or Content"
        description="A member, comment, or document that violates community guidelines."
        onClick={() => onSelect("member")}
      />
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<ModalStep>("pick");
  const [option, setOption] = useState<OptionKey | null>(null);
  const [problemKind, setProblemKind] = useState<ProblemKind | null>(null);

  function handleClose() {
    onClose();
    // Reset after animation settles
    setTimeout(() => {
      setStep("pick");
      setOption(null);
      setProblemKind(null);
    }, 200);
  }

  function handleOptionSelect(key: OptionKey) {
    if (key === "document") {
      // Documents are contributed through the Database, where files are cleaned and reviewed.
      handleClose();
      navigate("/database?contribute=1");
      return;
    }
    setOption(key);
    if (key !== "problem") {
      setStep("form");
    }
  }

  function handleProblemKind(kind: ProblemKind) {
    setProblemKind(kind);
    setStep("form");
  }

  function handleSuccess() {
    setStep("success");
  }

  function handleBack() {
    if (step === "form" && option === "problem" && problemKind) {
      setProblemKind(null);
      setOption("problem");
      setStep("pick"); // shows problem sub-picker
    } else {
      setStep("pick");
      setOption(null);
      setProblemKind(null);
    }
  }

  // Derive the title
  const titles: Record<string, string> = {
    feature: "Request a Feature",
    problem_bug: "Report a Bug",
    problem_member: "Report a Member or Content",
    document: "Share a Document",
  };

  const formKey =
    option === "problem" && problemKind
      ? `problem_${problemKind}`
      : option ?? "";

  const modalTitle =
    step === "pick" && !option
      ? "How can we help?"
      : step === "pick" && option === "problem"
      ? "Report a Problem"
      : step === "success"
      ? "Thank you!"
      : titles[formKey] ?? "Feedback";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40" />
        <DialogPrimitive.Content
          style={{ animationTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)" }}
          className="fixed bottom-32 right-4 md:bottom-20 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-[400px] max-h-[calc(100vh-12rem)] overflow-y-auto bg-white rounded-2xl border border-gray-100 shadow-2xl p-6 origin-bottom-right duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-2"
        >
        <DialogHeader>
          <div className="flex items-center gap-2 pr-6">
            {step !== "pick" && step !== "success" && (
              <button
                onClick={handleBack}
                className="text-gray-400 hover:text-gray-600 transition-colors mr-1 active:scale-[0.97]"
                aria-label="Go back"
              >
                ←
              </button>
            )}
            <DialogTitle>{modalTitle}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 active:scale-[0.97]"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>

        {/* Step 1: Main picker */}
        {step === "pick" && !option && (
          <div className="space-y-3 mt-2">
            <OptionCard
              icon="💡"
              title="Request a Feature"
              description="Have an idea that would improve Tappanon?"
              onClick={() => handleOptionSelect("feature")}
            />
            <OptionCard
              icon="🚩"
              title="Report a Problem"
              description="Found a bug or want to report a member or content?"
              onClick={() => handleOptionSelect("problem")}
            />
            <OptionCard
              icon="📄"
              title="Share a Document"
              description="Submit an HR document to add to the community library."
              onClick={() => handleOptionSelect("document")}
            />
          </div>
        )}

        {/* Step 1b: Problem sub-picker */}
        {step === "pick" && option === "problem" && (
          <div className="mt-2">
            <ProblemPicker onSelect={handleProblemKind} />
          </div>
        )}

        {/* Step 2: Forms */}
        {step === "form" && option === "feature" && (
          <div className="mt-2">
            <FeatureRequestForm onSuccess={handleSuccess} />
          </div>
        )}

        {step === "form" && option === "problem" && problemKind === "bug" && (
          <div className="mt-2">
            <BugReportForm onSuccess={handleSuccess} />
          </div>
        )}

        {step === "form" &&
          option === "problem" &&
          problemKind === "member" && (
            <div className="mt-2">
              <MemberReportForm onSuccess={handleSuccess} />
            </div>
          )}

        {/* Step 3: Success */}
        {step === "success" && (
          <div className="mt-2 py-6 text-center space-y-3">
            <div className="text-5xl">✅</div>
            <p className="font-semibold text-gray-900">Submitted!</p>
            <p className="text-sm text-gray-500 max-w-xs mx-auto">
              {option === "document"
                ? "Your document is under review. We'll notify you once it's processed."
                : option === "problem" && problemKind === "member"
                ? "Your report has been received and will be reviewed by our team."
                : "We've received your submission and will follow up soon."}
            </p>
            <Button
              onClick={handleClose}
              variant="outline"
              className="mt-2 rounded-xl"
            >
              Close
            </Button>
          </div>
        )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}
